import { Button, Empty, Select, Typography } from "antd"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { SearchBar } from "../../../components/Input/SearchBar.tsx"
import { FormChangedConfirm } from "../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { MAIN_CONTENT_STICKY_BOTTOM_CLASS } from "../../../components/main-content"
import ReactRouterPrompt from "../../../components/ReactRouterPrompt.tsx"
import { CRITICAL_GROUPS } from "../constants/notifications-settings"
import {
  type Channel,
  type ChannelValues,
  type DraftOverrides,
  type RowValues,
  applyPatch,
  categoryState,
  changeCount,
  channelState,
  currentRow,
  groupChannels,
  pruneOverrides,
  rowChannels,
  savedRows,
  searchSettings,
  sectionsOf,
  setAllPatch,
  setChannelPatch,
  submittedValues
} from "../helpers/notification-settings-matrix.ts"
import { type SmsLock } from "../sms/use-sms-verification"
import type {
  NotificationSettings,
  NotificationSettingsItem
} from "../types/notification.types"
import { NotificationGroupIcon } from "./NotificationGroupIcon"
import { CategoryToggle } from "./settings/CategoryToggle"
import {
  type MatrixSection,
  NotificationMatrix
} from "./settings/NotificationMatrix"

const { Text, Title } = Typography

type SaveStatus = "idle" | "saved" | "error"

interface NotificationSettingsFormProps {
  settings: NotificationSettings
  onFinish: (values: { [key: string]: ChannelValues }) => void
  saving: boolean
  smsLock?: SmsLock
  // Called when a single Desktop choice is turned on — the click the browser
  // needs before it will ask for permission.
  onDesktopTurnedOn?: () => void
  // Opens the channel setup when SMS is waiting on verification.
  onSmsSetup?: () => void
}

function useGroupLabel() {
  const { t } = useTranslation()
  return (key: string) =>
    t(`notifications.groups.${key}`, { defaultValue: key }) as string
}

function CategoryNav({
  keys,
  selected,
  searching,
  onSelect
}: {
  keys: string[]
  selected: string
  searching: boolean
  onSelect: (key: string) => void
}) {
  const { t } = useTranslation()
  const groupLabel = useGroupLabel()
  const refs = useRef<Record<string, HTMLButtonElement | null>>({})

  const focusAt = (index: number) => {
    const key = keys[(index + keys.length) % keys.length]
    onSelect(key)
    refs.current[key]?.focus()
  }

  return (
    <>
      <div className="md:hidden">
        <Select
          className="w-full"
          value={selected}
          onChange={onSelect}
          aria-label={t("notifications.settings.categoryLabel")}
          options={keys.map((key) => ({
            value: key,
            label: (
              <span className="inline-flex items-center gap-2">
                <NotificationGroupIcon groupKey={key} />
                {groupLabel(key)}
              </span>
            )
          }))}
        />
      </div>
      <nav
        className="hidden md:block w-56 shrink-0 border-e border-border pe-4"
        aria-label={t("notifications.settings.categoryLabel")}
      >
        <div
          role="tablist"
          aria-orientation="vertical"
          className="flex flex-col gap-1"
        >
          {keys.map((key, index) => {
            const active = !searching && key === selected
            return (
              <button
                key={key}
                ref={(el) => {
                  refs.current[key] = el
                }}
                type="button"
                role="tab"
                id={`notification-category-${key}`}
                aria-selected={active}
                aria-controls="notification-category-panel"
                tabIndex={key === selected ? 0 : -1}
                onClick={() => onSelect(key)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault()
                    focusAt(index + 1)
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault()
                    focusAt(index - 1)
                  } else if (e.key === "Home") {
                    e.preventDefault()
                    focusAt(0)
                  } else if (e.key === "End") {
                    e.preventDefault()
                    focusAt(keys.length - 1)
                  }
                }}
                className={`flex items-center gap-3 h-11 px-3 rounded-lg text-start text-sm transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  active
                    ? "bg-primary/10 text-primary-strong font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <NotificationGroupIcon groupKey={key} />
                <span className="truncate">{groupLabel(key)}</span>
              </button>
            )
          })}
        </div>
      </nav>
    </>
  )
}

function SaveFooter({
  count,
  saving,
  status,
  onDiscard,
  onSave
}: {
  count: number
  saving: boolean
  status: SaveStatus
  onDiscard: () => void
  onSave: () => void
}) {
  const { t } = useTranslation()
  if (!count && !saving && status === "idle") return null

  const message = saving
    ? t("notifications.settings.saving")
    : status === "error" && count
      ? t("notifications.settings.saveFailed")
      : count
        ? t("notifications.settings.unsavedChanges", { count })
        : t("notifications.settings.allSaved")

  const dot =
    status === "error" && count
      ? "bg-error"
      : count
        ? "bg-warning"
        : "bg-success"

  return (
    <div
      className={`${MAIN_CONTENT_STICKY_BOTTOM_CLASS} z-[3] -mx-2 lg:-mx-4 mt-4 px-2 lg:px-4 py-3 bg-white border-t border-border flex flex-wrap items-center justify-between gap-3`}
      data-testid="notification-settings-footer"
    >
      <span
        className="inline-flex items-center gap-2 text-sm text-gray-800"
        role="status"
        aria-live="polite"
      >
        <span aria-hidden="true" className={`size-2.5 rounded-full ${dot}`} />
        {message}
      </span>
      <span className="flex gap-3">
        <Button onClick={onDiscard} disabled={!count || saving}>
          {t("notifications.settings.discard")}
        </Button>
        <Button
          type="primary"
          onClick={onSave}
          disabled={!count}
          loading={saving}
        >
          {t("notifications.settings.saveChanges")}
        </Button>
      </span>
    </div>
  )
}

export function NotificationSettingsForm({
  settings,
  onFinish,
  saving,
  smsLock = null,
  onDesktopTurnedOn,
  onSmsSetup
}: NotificationSettingsFormProps) {
  const { t } = useTranslation()
  const groupLabel = useGroupLabel()
  const keys = useMemo(() => Object.keys(settings), [settings])

  const [overrides, setOverrides] = useState<DraftOverrides>({})
  const [selected, setSelected] = useState(keys[0] ?? "")
  const [query, setQuery] = useState("")
  const [status, setStatus] = useState<SaveStatus>("idle")

  const saved = useMemo(() => savedRows(settings), [settings])
  const count = changeCount(overrides, saved)
  const dirty = count > 0

  // A later answer from the server (the SMS defaults a first verification
  // switches on, or the save itself) replaces what the user has not changed,
  // and only that.
  useEffect(() => {
    setOverrides((current) => pruneOverrides(current, saved))
  }, [saved])

  useEffect(() => {
    if (!keys.includes(selected)) setSelected(keys[0] ?? "")
  }, [keys, selected])

  // The save thunk merges what it sent into `settings` only when the server
  // took it, so a draft still differing once saving ends is a refused save.
  const wasSaving = useRef(saving)
  useEffect(() => {
    if (wasSaving.current && !saving) setStatus(dirty ? "error" : "saved")
    wasSaving.current = saving
  }, [saving, dirty])

  useEffect(() => {
    if (status !== "saved") return
    const timer = setTimeout(() => setStatus("idle"), 3000)
    return () => clearTimeout(timer)
  }, [status])

  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [dirty])

  const rows: RowValues = useMemo(() => {
    const result: RowValues = {}
    Object.values(settings)
      .flat()
      .forEach((item) => {
        result[item.notification_type] ??= currentRow(item, overrides)
      })
    return result
  }, [settings, overrides])

  const patch = (next: RowValues) => {
    if (status === "error") setStatus("idle")
    setOverrides((current) => applyPatch(current, next, saved))
  }

  const toggle = (
    item: NotificationSettingsItem,
    channel: Channel,
    next: boolean
  ) => {
    patch({ [item.notification_type]: { [channel]: next } })
    if (channel === "desktop" && next) onDesktopTurnedOn?.()
  }

  const selectCategory = (key: string) => {
    setSelected(key)
    setQuery("")
  }

  const save = () => onFinish(submittedValues(settings, overrides))
  const discard = () => {
    setOverrides({})
    setStatus("idle")
  }

  const searching = query.trim().length > 0
  const items = settings[selected] ?? []
  const isCritical = CRITICAL_GROUPS.includes(selected)
  const results = searching ? searchSettings(settings, query) : []

  const shownItems = searching ? results.flatMap(([, list]) => list) : items
  const columns = groupChannels(shownItems)
  const sections: MatrixSection[] = searching
    ? results.map(([key, list]) => ({
        key,
        heading: (
          <span className="inline-flex items-center gap-2 normal-case tracking-normal text-sm text-gray-800">
            <NotificationGroupIcon groupKey={key} />
            {groupLabel(key)}
          </span>
        ),
        items: list,
        isCritical: CRITICAL_GROUPS.includes(key)
      }))
    : sectionsOf(selected, items).map((section) => ({
        key: section.key,
        heading: section.key
          ? t(`notifications.settings.subgroups.${section.key}`)
          : undefined,
        items: section.items,
        isCritical
      }))

  const hasUnsupported = shownItems.some((item) =>
    columns.some((c) => !rowChannels(item).includes(c))
  )
  const disabled = saving

  const setCategory = (next: boolean) =>
    patch(setAllPatch(items, rows, next, isCritical, smsLock))

  if (!keys.length) {
    return <Empty description={t("notifications.settings.noSettings")} />
  }

  return (
    <div className="flex flex-col flex-1 w-full gap-3">
      <SearchBar
        size="large"
        className="w-full md:!w-[360px]"
        style={{ flex: "none", minWidth: 0 }}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("notifications.settings.searchPlaceholder")}
        aria-label={t("notifications.settings.searchLabel")}
      />
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 flex-1">
        <CategoryNav
          keys={keys}
          selected={selected}
          searching={searching}
          onSelect={selectCategory}
        />
        <section
          id="notification-category-panel"
          role={searching ? "region" : "tabpanel"}
          aria-labelledby={
            searching ? undefined : `notification-category-${selected}`
          }
          aria-label={
            searching ? t("notifications.settings.searchResults") : undefined
          }
          className="flex-1 min-w-0 flex flex-col gap-3"
        >
          {searching ? (
            <div>
              <Title level={4} className="!mb-0 text-start">
                {t("notifications.settings.searchResults")}
              </Title>
              <Text type="secondary" role="status">
                {t("notifications.settings.searchCount", {
                  count: shownItems.length,
                  query: query.trim()
                })}
              </Text>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <div>
                <Title level={4} className="!mb-0 text-start">
                  {groupLabel(selected)}
                </Title>
                <Text type="secondary">
                  {t(`notifications.settings.descriptions.${selected}`, {
                    defaultValue: t(
                      "notifications.settings.descriptionFallback",
                      { group: groupLabel(selected) }
                    )
                  })}
                </Text>
              </div>
              <CategoryToggle
                state={categoryState(items, rows, smsLock)}
                disabled={disabled}
                onChange={setCategory}
              />
            </div>
          )}

          {searching && !results.length ? (
            <Empty
              className="py-10"
              description={t("notifications.settings.searchEmpty", {
                query: query.trim()
              })}
            >
              <Button onClick={() => setQuery("")}>
                {t("notifications.settings.clearSearch")}
              </Button>
            </Empty>
          ) : (
            <>
              <NotificationMatrix
                sections={sections}
                columns={columns}
                rows={rows}
                smsLock={smsLock}
                disabled={disabled}
                onToggle={toggle}
                onSmsSetup={onSmsSetup}
                bulk={
                  searching
                    ? undefined
                    : {
                        label: groupLabel(selected),
                        states: Object.fromEntries(
                          columns.map((c) => [c, channelState(items, rows, c)])
                        ),
                        onChannel: (channel, next) =>
                          patch(
                            setChannelPatch(
                              items,
                              rows,
                              channel,
                              next,
                              isCritical
                            )
                          )
                      }
                }
              />
              <div className="flex flex-col gap-1 text-xs text-gray-500 italic">
                {hasUnsupported && (
                  <span>
                    <span aria-hidden="true" className="not-italic me-2">
                      —
                    </span>
                    {t("notifications.settings.notAvailable")}
                  </span>
                )}
                {(searching
                  ? sections.some((s) => s.isCritical)
                  : isCritical) && (
                  <span>{t("notifications.settings.criticalLocked")}</span>
                )}
              </div>
            </>
          )}
        </section>
      </div>
      <SaveFooter
        count={count}
        saving={saving}
        status={status}
        onDiscard={discard}
        onSave={save}
      />
      <ReactRouterPrompt
        when={({ currentLocation, nextLocation }) =>
          dirty && currentLocation.pathname !== nextLocation.pathname
        }
      >
        {({ isActive, onConfirm, onCancel }) => (
          <FormChangedConfirm
            open={isActive}
            onOk={onConfirm}
            onCancel={onCancel}
          />
        )}
      </ReactRouterPrompt>
    </div>
  )
}
